import React from 'react';
import { UserProfile } from './UserProfile';
import { AppLauncher } from './AppLauncher';
import { SystemTray } from './SystemTray';
import type { PhoneSlotId, PhoneSlotProfile } from './PhoneSlotStrip';

interface OpenWindowItem {
  id: string;
  title: string;
  type: string;
  isMinimized: boolean;
}

interface CockpitTaskbarProps {
  theme: 'dark' | 'light' | 'purple';
  activeRole: string;
  environmentName: string;
  openWindows: OpenWindowItem[];
  activeWindowId: string | null;
  desktopTime: string;
  rolePrimary?: string;
  roleSecondary?: string;
  /** Esconde a taskbar (desliza para baixo) — usada com janela maximizada. */
  autoHidden?: boolean;
  activePhoneProfiles?: string[];
  onTogglePhoneSlot?: (slot: PhoneSlotId, profile: PhoneSlotProfile) => void;
  onThemeToggle: () => void;
  onLogout: () => void;
  onStartMenuToggle: () => void;
  onFocusWindow: (id: string) => void;
  onMinimizeWindow: (id: string) => void;
  onOpenApp: (appType: 'documents' | 'planilhas' | 'apresentacoes') => void;
  onRoleSelect: (roleKey: string) => void;
  onActorSelect?: (roleKey: string, actorId: string, actorName: string) => void;
  onOpenProfile?: () => void;
  onOpenSettings?: () => void;
}

export const CockpitTaskbar: React.FC<CockpitTaskbarProps> = ({
  theme,
  activeRole,
  environmentName,
  openWindows,
  activeWindowId,
  desktopTime,
  rolePrimary = '#9b6cff',
  roleSecondary = '#3de5ff',
  autoHidden = false,
  activePhoneProfiles = [],
  onTogglePhoneSlot,
  onThemeToggle,
  onLogout,
  onStartMenuToggle,
  onFocusWindow,
  onMinimizeWindow,
  onOpenApp,
  onRoleSelect,
  onActorSelect,
  onOpenProfile,
  onOpenSettings,
}) => {
  // Converte hex para RGB para uso em rgba
  const hexToRgb = (hex: string): string => {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  };

  const primaryRgb = hexToRgb(rolePrimary);
  const secondaryRgb = hexToRgb(roleSecondary);

  // Taskbar background adapta ao ambiente com borda e glow sutil
  const taskbarStyle: React.CSSProperties = theme === 'light'
    ? {
        background: 'rgba(255, 255, 255, 0.80)',
        borderColor: 'rgba(203, 213, 225, 0.6)',
        boxShadow: '0 28px 90px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.9)',
      }
    : {
        background: `linear-gradient(90deg, rgba(${primaryRgb}, 0.08) 0%, rgba(13, 11, 26, 0.78) 30%, rgba(13, 11, 26, 0.78) 70%, rgba(${secondaryRgb}, 0.06) 100%)`,
        borderColor: `rgba(${primaryRgb}, 0.18)`,
        boxShadow: `0 28px 90px rgba(0,0,0,0.46), inset 0 1px 0 rgba(255,255,255,0.06), 0 0 30px rgba(${primaryRgb}, 0.08)`,
      };

  const textClass = theme === 'light' ? 'text-[#0f172a]' : 'text-white';

  return (
    <footer 
      className={`fixed bottom-[14px] left-[16px] right-[16px] h-[64px] px-3.5 flex items-center justify-between gap-4 z-[9999] backdrop-blur-[30px] saturate-[155%] border rounded-[23px] transition-all duration-300 ease-out ${textClass} ${
        autoHidden
          ? 'translate-y-[calc(100%+28px)] opacity-0 pointer-events-none'
          : 'translate-y-0 opacity-100'
      }`}
      style={taskbarStyle}
      aria-hidden={autoHidden}
    >
      {/* Zona Esquerda: Iniciar & Seletor de Perfil Integrado */}
      <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
        <UserProfile
          activeRole={activeRole}
          theme={theme}
          onStartMenuToggle={onStartMenuToggle}
          onRoleSelect={onRoleSelect}
          onActorSelect={onActorSelect}
          rolePrimary={rolePrimary}
          roleSecondary={roleSecondary}
          onOpenProfile={onOpenProfile}
          onOpenSettings={onOpenSettings}
        />
      </div>

      {/* Zona Central: Aplicativos Ativos */}
      <div className="flex-1 max-w-lg hidden md:block">
        <AppLauncher
          activeRoleKey={activeRole}
          openWindows={openWindows}
          activeWindowId={activeWindowId}
          onFocusWindow={onFocusWindow}
          onMinimizeWindow={onMinimizeWindow}
          onOpenApp={onOpenApp}
        />
      </div>

      {/* Zona Direita: Celulares EMU + Relógio */}
      <div className="flex-shrink-0">
        <SystemTray
          theme={theme}
          onThemeToggle={onThemeToggle}
          onLogout={onLogout}
          desktopTime={desktopTime}
          activePhoneProfiles={activePhoneProfiles}
          onTogglePhoneSlot={onTogglePhoneSlot}
        />
      </div>
    </footer>
  );
};
